import { ISelectionGeometryMessage } from "@kuika/kuika-cl-model";
import React, { Component } from "react";
import { selectionGeometryStore } from "./selection-geometry-store";

export interface ISelectionOverlayErrorBoundaryProps {
  children: React.ReactNode;
}

interface ISelectionOverlayErrorBoundaryState {
  hasError: boolean;
}

/**
 * Keeps a render failure in the selection overlay (a component set newer than this designer, a bad
 * value) from unmounting the whole designer: the overlay just disappears. It comes back on the next
 * selection — a failure tied to one component must not hide the bar for the rest of the session.
 */
export class SelectionOverlayErrorBoundary extends Component<
  ISelectionOverlayErrorBoundaryProps,
  ISelectionOverlayErrorBoundaryState
> {
  state: ISelectionOverlayErrorBoundaryState = { hasError: false };

  /** The selection that failed to render; a different one gets a fresh try. */
  private failedSelectionKey: string | null = null;

  private unsubscribe: (() => void) | null = null;

  static getDerivedStateFromError(): ISelectionOverlayErrorBoundaryState {
    return { hasError: true };
  }

  componentDidMount(): void {
    this.unsubscribe = selectionGeometryStore.subscribe(this.retryOnNewSelection);
  }

  componentDidCatch(error: Error): void {
    this.failedSelectionKey = selectionKeyOf(selectionGeometryStore.getSnapshot());
    console.error("[SelectionOverlay] render failed; hiding the overlay until the selection changes.", error);
  }

  componentWillUnmount(): void {
    this.unsubscribe?.();
  }

  private retryOnNewSelection = (): void => {
    if (this.state.hasError && selectionKeyOf(selectionGeometryStore.getSnapshot()) !== this.failedSelectionKey) {
      this.failedSelectionKey = null;
      this.setState({ hasError: false });
    }
  };

  render(): React.ReactNode {
    return this.state.hasError ? null : this.props.children;
  }
}

const selectionKeyOf = (geometry: ISelectionGeometryMessage | null): string =>
  geometry === null ? "-" : `${geometry.kind}:${geometry.kuikaId ?? ""}:${geometry.selectedKuikaIds.join(",")}`;
